import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { Addon, Cart } from 'src/services/types/joinEventV2'
import { emptyCart, getDerivedBundleAndCartState } from './utils'

type SyncStatePayload = {
    old_state: any
    rewards: any[]
    addons: any[]
    cart: any
    challengeId: number
    summary: any
}

type Summary = {
    challengeId: number
    name: string
    challengeUrl: string
    description: string
    imageUrl: string
    price: number
    totalDistance: number
    googleDirectionsResult: string
    challengeData: any[]
    isOldStyleChallenge: boolean
    discount: number
    fundraisingMessage: string
    rightMediaBlock: string
    minimumPrice: number
    addonDiscount: any
    waiverAccepted: string
    logo: string
    isSwag: boolean
    is30Day: boolean
    isAddonOptional?: boolean
    isAddonOptionalReason?: string
    hasAccessMessage?: string
    hasAccess?: boolean
}

interface JoinEventState {
    challengeId: number
    summary: Summary
    rewards: Addon[]
    addons: Addon[]
    cart: Partial<Cart>
    receipt: Partial<Cart>
    showReceipt: boolean
    salesTax: {
        taxSales: number
        geoCounty: string | null
        lastAddress: any | null
    }
    stepProgress: number
    stepNumber: number
    selectedRewards: any[]
    selectedAddons: any[]
    shippingInfo: any | null
    error: string | null
}

const initialState: JoinEventState = {
    challengeId: 0,
    summary: {
        challengeId: 0,
        name: '',
        challengeUrl: '',
        description: '',
        imageUrl: '',
        price: 0.0,
        totalDistance: 0.0,
        googleDirectionsResult: '',
        challengeData: [],
        isOldStyleChallenge: false,
        discount: 0,
        fundraisingMessage: '',
        rightMediaBlock: '',
        minimumPrice: 0.0,
        addonDiscount: null,
        waiverAccepted: null,
        logo: '',
        isSwag: false,
        is30Day: false,
        isAddonOptional: null,
        isAddonOptionalReason: null
    },
    rewards: [],
    addons: [],
    cart: emptyCart,
    receipt: emptyCart,
    showReceipt: false,
    salesTax: {
        taxSales: 0.0,
        geoCounty: null,
        lastAddress: {}
    },
    stepProgress: 0,
    stepNumber: 0,
    selectedRewards: [],
    selectedAddons: [],
    shippingInfo: null,
    error: null
    // statesLoading: false,
    // statesData: [],
    // statesError: null,
    // salesTaxLoading: false,
    // salesTaxError: null,
    // salesTaxData: null
    // voucherState: {
    //     loading: false,
    //     error: null
    // },
    // promotionState: {
    //     loading: false,
    //     error: null
    // },
    // checkoutState: {
    //     loading: false,
    //     error: null
    // },
    // isSaving: false,
    // inNativePay: false
}

const joinEventSlice = createSlice({
    name: 'joinevent',
    initialState: initialState,
    reducers: {
        hydrateBundle(state, action) {
            state.cart.bundle = action.payload
        },
        syncJoinEventState(state, action: PayloadAction<SyncStatePayload>) {
            const newState = getDerivedBundleAndCartState(
                action.payload.old_state,
                action
            )

            state.summary = {
                ...action.payload.summary,
                googleDirectionsResult: ''
            }
            state.challengeId = action.payload.challengeId
            state.rewards = action.payload.rewards
            state.addons = action.payload.addons
            state.cart = {
                ...state.cart,
                ...newState.cart
            }
            state.receipt = newState.receipt
        },
        throwError(state, action) {
            state.error = action.payload
        },
        updateSelectedRewards(state, action) {
            state.selectedRewards = action.payload
        },
        updateSelectedAddons(state, action) {
            state.selectedAddons = action.payload
        },
        updateShippingInfo(state, action) {
            state.shippingInfo = action.payload
            state.cart.address = action.payload.shippingAddress
            state.cart.city = action.payload.city
            state.cart.stateId = action.payload.stateId
            state.cart.zip = action.payload.zipCode
        },
        updateRewardSelections(state, action) {
            state.rewards = action.payload
        },
        updateAddonSelections(state, action) {
            state.addons = action.payload
        },
        updateCart(state, action) {
            state.cart = action.payload
        },
        clearBundle(state) {
            state.cart.bundle = emptyCart.bundle
        },
        clearVouchers(state) {
            state.cart.vouchers = []
        },
        updateSalesTax(state, action) {
            state.salesTax = action.payload
        },
        updateVouchers(state, action) {
            state.cart.vouchers = action.payload
        },
        updateCartToken(state, action) {
            state.cart.token = action.payload
        },
        updateCartPaymentMethod(state, action) {
            state.cart.paymentMethod = action.payload
        },
        updateAuthorizedId(state, action) {
            state.cart.authorizeID = action.payload
        },
        updateCartIsApproved(state, action) {
            state.cart.isApproved = action.payload
        },
        updatePayerId(state, action) {
            state.cart.payerID = action.payload
        },
        updateOrderId(state, action) {
            state.cart.orderID = action.payload
        },
        checkoutComplete(state, action) {
            state.cart = emptyCart
            state.receipt = action.payload.receipt
            state.showReceipt = true
        },
        updateOptionClicked(
            state,
            action: PayloadAction<{
                challengeAddonId: string | number
                optionId: number
                displayText: string
            }>
        ) {
            const rewardToUpdate = state.rewards.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )
            const addonToUpdate = state.addons.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )

            if (rewardToUpdate !== -1) {
                state.rewards[rewardToUpdate].challengeAddonOptionId =
                    action.payload.optionId

                state.rewards[rewardToUpdate].challengeAddonOptionName =
                    action.payload.displayText
            }

            if (addonToUpdate !== -1) {
                state.addons[addonToUpdate].challengeAddonOptionId =
                    action.payload.optionId

                state.addons[addonToUpdate].challengeAddonOptionName =
                    action.payload.displayText
            }
        },
        saveOptionSelection(
            state,
            action: PayloadAction<{
                challengeAddonId: string | number
            }>
        ) {
            const rewardToUpdate = state.rewards.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )
            const addonToUpdate = state.addons.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )

            if (rewardToUpdate !== -1) {
                state.rewards[rewardToUpdate].selected = true
            }

            if (addonToUpdate !== -1) {
                state.addons[addonToUpdate].selected = true
            }
        },
        removeOptionSelection(
            state,
            action: PayloadAction<{
                challengeAddonId: string | number
            }>
        ) {
            const rewardToUpdate = state.rewards.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )
            const addonToUpdate = state.addons.findIndex(
                (draftItem) =>
                    draftItem.challengeAddonId === action.payload.challengeAddonId
            )

            if (rewardToUpdate !== -1) {
                state.rewards[rewardToUpdate].selected = false
            }

            if (addonToUpdate !== -1) {
                state.addons[addonToUpdate].selected = false
            }
        }
    },
    extraReducers: {
        [HYDRATE]: (state, action: PayloadAction<any>) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return {
                ...state,
                ...action.payload.joinevent,
                cart: {
                    ...state.cart,
                    ...action.payload.joinevent?.cart,
                    bundle: {
                        ...state.cart.bundle
                    },
                    serverBundle: action?.payload?.joinevent?.cart?.bundle ?? null
                }
            }
        }
    }
})

const { actions } = joinEventSlice

export const {
    syncJoinEventState,
    updateSelectedAddons,
    updateSelectedRewards,
    updateShippingInfo,
    updateRewardSelections,
    updateAddonSelections,
    updateCart,
    clearBundle,
    updateVouchers,
    updateCartIsApproved,
    updateCartPaymentMethod,
    updateCartToken,
    clearVouchers,
    updateOptionClicked,
    saveOptionSelection,
    hydrateBundle,
    updateSalesTax,
    checkoutComplete,
    removeOptionSelection,
    throwError,
    updateAuthorizedId,
    updatePayerId,
    updateOrderId
} = actions

export default joinEventSlice
